import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import MobileMenu from '../components/MobileMenu';
import Logo from '../components/Logo';
import useSEO from '../hooks/useSEO';

const PrivacyPage = () => {
  const navigate = useNavigate();

  // SEO optimization
  useSEO({
    title: 'Integritetspolicy - YourTherapist | GDPR-kompatibel AI-terapi',
    description: 'Läs vår integritetspolicy för AI-driven terapi. GDPR-kompatibel, end-to-end krypterad och säker hantering av dina personuppgifter och terapisamtal.',
    keywords: 'integritetspolicy, GDPR, dataskydd, säkerhet, kryptering, personuppgifter, terapi säkerhet',
    image: 'https://www.yourtherapist.se/logo512.png',
    url: 'https://www.yourtherapist.se/privacy',
    type: 'website'
  });

  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Top Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center h-16">
            <Logo 
              size="large"
              onClick={handleBackToHome}
              textColor="text-gray-700"
              hoverTextColor="hover:text-pink-600"
            />
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              <button
                onClick={handleBackToHome}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-pink-600 transition-colors font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Tillbaka hem
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <MobileMenu />
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <section className="pt-24 pb-16 w-full">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Integritetspolicy
          </h1>
          <p className="text-gray-600 mb-2">
            Senast uppdaterad: 1 januari 2025
          </p>
          <p className="text-gray-700 mb-12 leading-relaxed">
            Your Therapist tar din integritet på största allvar. Denna integritetspolicy beskriver hur vi samlar in, använder, delar och skyddar dina personuppgifter när du använder vår tjänst för digital terapi och psykologisk vård.
          </p>

          <div className="space-y-12">
            {/* Section 1 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Personuppgiftsansvarig</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                Your Therapist är personuppgiftsansvarig för behandlingen av dina personuppgifter. Vi är registrerade i Sverige och följer svensk och europeisk dataskyddslagstiftning (GDPR).
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Kontaktuppgifter:</strong><br />
                Your Therapist<br />
                Riddargatan 70<br />
                114 57 Stockholm<br />
                E-post: info@yourtherapist.se
              </p>
            </div>

            {/* Section 2 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Vilka personuppgifter samlar vi in?</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Vi samlar in och behandlar följande typer av personuppgifter:
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Kontouppgifter</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
                <li>Namn (för- och efternamn)</li>
                <li>E-postadress</li>
                <li>Lösenord (krypterat)</li>
                <li>Telefonnummer (frivilligt)</li>
                <li>Profilbild (frivilligt)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Hälsouppgifter</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
                <li>Samtalshistorik med AI-terapeut</li>
                <li>Kommunikation med legitimerad terapeut</li>
                <li>Anteckningar och journaluppgifter</li>
                <li>Målsättningar och framstegsinformation</li>
                <li>Anamnesuppgifter (om du väljer att dela med dig)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Teknisk information</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
                <li>IP-adress</li>
                <li>Webbläsartyp och version</li>
                <li>Enhetstyp och operativsystem</li>
                <li>Språkinställningar</li>
                <li>Användningsstatistik och aktivitetsloggar</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Betalningsinformation</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Faktureringsinformation (via säker betalningspartner)</li>
                <li>Transaktionshistorik</li>
                <li>Abonnemangsuppgifter</li>
              </ul>
            </div>

            {/* Section 3 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Hur använder vi dina personuppgifter?</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Vi använder dina personuppgifter för följande ändamål:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Tillhandahålla och administrera terapitjänster</li>
                <li>Matcha dig med lämplig terapeut</li>
                <li>Kommunicera med dig om din vård och tjänsten</li>
                <li>Säkerställa säkerhet och förhindra missbruk</li>
                <li>Förbättra och utveckla våra AI-algoritmer och tjänster</li>
                <li>Hantera betalningar och fakturering</li>
                <li>Uppfylla juridiska förpliktelser</li>
                <li>Genomföra anonymiserad forskning (med ditt samtycke)</li>
              </ul>
            </div>

            {/* Section 4 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Rättslig grund för behandling</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Vi behandlar dina personuppgifter baserat på följande rättsliga grunder:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>Fullgörande av avtal:</strong> För att tillhandahålla de terapitjänster du beställt</li>
                <li><strong>Samtycke:</strong> För behandling av känsliga hälsouppgifter och forskning</li>
                <li><strong>Berättigat intresse:</strong> För att förbättra tjänsten, säkerhet och teknisk support</li>
                <li><strong>Rättslig förpliktelse:</strong> För att uppfylla patientdatalagen och annan lagstiftning</li>
              </ul>
            </div>

            {/* Section 5 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Datasäkerhet</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Vi använder branschledande säkerhetsåtgärder för att skydda dina uppgifter:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>End-to-end kryptering:</strong> All kommunikation mellan dig och terapeuter är krypterad så att endast ni kan läsa innehållet</li>
                <li><strong>AES-256 kryptering:</strong> Data i vila krypteras med militär standard</li>
                <li><strong>TLS 1.3:</strong> All datatrafik skyddas med senaste krypteringsprotokoll</li>
                <li><strong>Multifaktorautentisering:</strong> Tillgängligt för alla användare</li>
                <li><strong>Regelbundna säkerhetsrevisioner:</strong> Oberoende granskningar av våra system</li>
                <li><strong>Begränsad åtkomst:</strong> Endast auktoriserad personal har tillgång till känsliga system</li>
                <li><strong>Automatisk utloggning:</strong> För att förhindra obehörig åtkomst</li>
              </ul>
            </div>

            {/* Section 6 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Delning av personuppgifter</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Vi säljer aldrig dina personuppgifter. Vi delar endast information i begränsade fall:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>Med din terapeut:</strong> Relevanta samtals- och hälsouppgifter delas med den legitimerade terapeut du matchats med</li>
                <li><strong>Tekniska leverantörer:</strong> Molntjänster inom EU för hosting och backup (med krypterade data)</li>
                <li><strong>Betalningspartners:</strong> Stripe och andra betalningsprocessorer för att hantera transaktioner</li>
                <li><strong>Juridiska krav:</strong> När vi är skyldiga enligt lag eller domstolsbeslut</li>
                <li><strong>Nödsituationer:</strong> Vid akut livsfara kan information delas med vård- eller akutmyndigheter</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Alla våra leverantörer är bundna av dataskyddsavtal och får endast behandla uppgifter enligt våra instruktioner.
              </p>
            </div>

            {/* Section 7 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Lagring och lagringstid</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Vi lagrar dina personuppgifter så länge det är nödvändigt:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>Kontouppgifter:</strong> Så länge du har ett aktivt konto, sedan 3 år efter avslut</li>
                <li><strong>Hälso- och journaluppgifter:</strong> 10 år efter senaste vårdkontakt (enligt patientdatalagen)</li>
                <li><strong>Betalningsuppgifter:</strong> 7 år (enligt bokföringslagen)</li>
                <li><strong>Tekniska loggar:</strong> Maximalt 2 år</li>
                <li><strong>Anonymiserad forskning:</strong> Kan lagras indefinitivt utan koppling till dig som person</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                All data lagras på säkra servrar inom EU. Vi använder inte servrar utanför EU/EES.
              </p>
            </div>

            {/* Section 8 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Dina rättigheter</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Enligt GDPR har du följande rättigheter:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>Rätt till tillgång:</strong> Få en kopia av de personuppgifter vi har om dig</li>
                <li><strong>Rätt till rättelse:</strong> Korrigera felaktiga eller ofullständiga uppgifter</li>
                <li><strong>Rätt till radering:</strong> Begära att vi raderar dina uppgifter (med vissa undantag enligt patientdatalagen)</li>
                <li><strong>Rätt till begränsning:</strong> Begära att vi begränsar behandlingen av dina uppgifter</li>
                <li><strong>Rätt till dataportabilitet:</strong> Få dina uppgifter i ett strukturerat, maskinläsbart format</li>
                <li><strong>Rätt att invända:</strong> Motsätta dig viss behandling av dina personuppgifter</li>
                <li><strong>Rätt att återkalla samtycke:</strong> När behandling baseras på samtycke kan du när som helst återkalla det</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                För att utöva dina rättigheter, kontakta oss på <strong>info@yourtherapist.se</strong>. Vi svarar inom 30 dagar.
              </p>
            </div>

            {/* Section 9 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Cookies</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Vi använder cookies och liknande tekniker för att:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Hålla dig inloggad mellan sessioner</li>
                <li>Komma ihåg dina preferenser och inställningar</li>
                <li>Förbättra säkerheten</li>
                <li>Analysera hur tjänsten används (med anonymiserad data)</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Du kan när som helst hantera cookies i din webbläsares inställningar. Observera att vissa funktioner kan begränsas om du blockerar cookies.
              </p>
            </div>

            {/* Section 10 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Ändringar i integritetspolicyn</h2>
              <p className="text-gray-700 leading-relaxed">
                Vi kan komma att uppdatera denna integritetspolicy. Väsentliga ändringar meddelas via e-post eller i tjänsten. Fortsatt användning efter ändringar innebär att du accepterar den uppdaterade policyn. Vi rekommenderar att du regelbundet läser igenom denna policy.
              </p>
            </div>

            {/* Section 11 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Klagomål och tillsyn</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Om du anser att vi behandlar dina personuppgifter felaktigt har du rätt att lämna in ett klagomål till tillsynsmyndigheten:
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Integritetsskyddsmyndigheten (IMY)</strong><br />
                Box 8114<br />
                104 20 Stockholm<br />
                E-post: imy@imy.se<br />
                Telefon: 08-657 61 00
              </p>
            </div>

            {/* Section 12 */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Kontakta oss</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Om du har frågor om denna integritetspolicy eller hur vi behandlar dina personuppgifter, kontakta oss gärna:
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>E-post:</strong> info@yourtherapist.se<br />
                <strong>Postadress:</strong> Your Therapist, Riddargatan 70, 114 57 Stockholm
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                Vi svarar normalt inom 2 arbetsdagar.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;
